import { LinearProgress } from "@material-ui/core";
import React, { useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import API from "../../../../../app/utils/api";
import { WarningDialog } from "../../../../../app/components";
import { BUTTON_LABELS } from "../../../../../app/components/warning.dialog/messages";
import PrimaryButton from "../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../app/components/buttons/outlined";
import FileUploader from "../../../../../app/components/file.uploader";
import CustomModal from "../../../../../app/components/modal.no.close";
import { UPLOAD_SOE_TABLE_WARNING_MESSAGE } from "../../../../../app/components/warning.dialog/messages";
import { useScheduleEvents } from "../../../../../app/contexts/schedule.events.context";
import { useSnackbar } from "../../../../../app/contexts/snackbar.context";
import { processSoeData } from "./process.soe.data";

const UploadSoeTableModal = ({ 
  onContinue, 
  studyId, 
  scheduleId,
  open,
  onClose,
}) => {
  const { showSnackbar } = useSnackbar();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [soeData, setSoeData] = useState(null);
  const { scheduleOfEventsAssessments, scheduleOfEventsVisits } = useScheduleEvents();

  const handleWarningConfirm = async () => {
    setShowWarning(false);
    // Change this call with the correct call to upload the data
    const response = await API.createSOEAssessmentVisitMappingUpload(soeData, studyId);

    if (response.status === 200) {
      showSnackbar("SOE table uploaded successfully", "success");
      setProgress(100);
      setStatus("Upload completed.");
      setApiStatus(200);
    } else {
      showSnackbar("Failed to upload SOE table", "error");
      setProgress(100);
      setStatus("Upload failed.");
      setApiStatus(response.status);
      setUploadErrors([
        response.data?.message || "Unknown error occurred",
      ]);
    }
  };

  const handleWarningCancel = () => {
    showSnackbar("Upload cancelled", "warning");
    setProgress(100);
    setStatus("Upload cancelled.");
    setApiStatus("warning");
    setShowWarning(false);
    setLoading(false);
  };

  const handleChange = async (files) => {
    if (files.length === 0) return;
    handleUpload(files);
  };

  const handleUpload = async (files) => {
    if (files.length === 0) return;

    setLoading(true);
    setProgress(0);
    setStatus("Processing SOE...");
    setUploadErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          cellDates: true,
          cellNF: true,
          cellStyles: true,
        });

        // Find the 'SOE' sheet
        const soeWorksheet = workbook.Sheets["SOE"];
        if (!soeWorksheet) {
          showSnackbar(
            "Could not find 'SOE' sheet in the workbook",
            "error",
          );
          setLoading(false);
          setUploadErrors([
            "Could not find 'SOE' sheet in the workbook",
          ]);
          return;
        }

        try {
          const soeGridData = processSoeData(
            soeWorksheet,
            showSnackbar,
            studyId, 
            scheduleId,
            scheduleOfEventsAssessments,
            scheduleOfEventsVisits,
          );

          if (soeGridData?.error) {
            setLoading(false);
            setUploadErrors([soeGridData.error]);
            return;
          }

          // Extract data and warnings from response
          const data = soeGridData;

          setProgress(50);
          setSoeData(data);
          setShowWarning(true);
        } catch (error) {
          setUploadErrors([error.message]);
          setProgress(100);
          setStatus("Upload failed.");
          setApiStatus("error");
        }
      };
      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      showSnackbar("Failed to process file", "error");
      setLoading(false);
      setUploadErrors([error.message]);
    }
  };

  const handleContinue = () => {
    onClose();
    onContinue();
  };

  return (
    <CustomModal
      size={700}
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="Upload SOE Table"
      aria-describedby="upload-soe-table"
      title="Upload SOE Table"
    >
      <Container>
        {!loading && (
          <VContainer>
            <FileUploader
              acceptedFiles={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [],
              }}
              onFileUpload={handleChange}
            />
          </VContainer>
        )}
        {loading && (
          <VContainer>
            {status !== "Upload completed." && status !== "Upload failed." && (
              <LinearProgress
                variant="determinate"
                value={progress}
              />
            )}
            <StatusText>{status}</StatusText>
            {uploadErrors.length > 0 && (
              <ErrorContainer>
                <ErrorTitle>Errors Found:</ErrorTitle>
                {uploadErrors.map((error, index) => (
                  <ErrorText key={index}>{error}</ErrorText>
                ))}
              </ErrorContainer>
            )}
          </VContainer>
        )}
        <ButtonContainer>
          {loading && apiStatus === 200 ? (
            <PrimaryButton onClick={handleContinue}>Continue</PrimaryButton>
          ) : (
            <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          )}
        </ButtonContainer>
      </Container>
      <WarningDialog
        open={showWarning}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
        message={UPLOAD_SOE_TABLE_WARNING_MESSAGE}
        confirmLabel={BUTTON_LABELS.UPLOAD}
      />
    </CustomModal>
  );
};

export default UploadSoeTableModal;

const VContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding-top: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding: 1em;
  padding-bottom: 0em;
  padding-top: 0;
  max-height: 75vh;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
`;

const StatusText = styled.div`
  font-weight: 500;
  margin-bottom: 1em;
`;

const ErrorContainer = styled.div`
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  padding: 1em;
  margin-top: 1em;
`;

const ErrorTitle = styled.div`
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 0.5em;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.9em;
  margin-bottom: 0.25em;
`;
